import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import PartnerService from '../../../services/PartnerService';

const PartnerByBranchSearchSelect = ({
  value = '',
  onChange,
  companyId = null,
  branchId = null,
  placeholder = 'Search and select partner...',
  name = 'partnerId',
  label = 'Partner',
  required = false,
  disabled = false,
  className = '',
  showLabel = true,
  allowClear = true,
  multiple = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (companyId && branchId) {
      loadPartners();
    } else {
      setOptions([]);
      setFilteredOptions([]);
      setSelectedOption(null);
      setSelectedOptions([]);
    }
  }, [companyId, branchId]);

  useEffect(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value : value ? [value] : [];
      const selected = options.filter((opt) => values.includes(opt.id));
      setSelectedOptions(selected);
    } else {
      const selected = options.find((opt) => opt.id === value) || null;
      setSelectedOption(selected);
      setSearchTerm(selected ? `${selected.fullName} (${selected.mobileNumber || ''})` : '');
    }
  }, [value, options, multiple]);

  useEffect(() => {
    if (!searchTerm) setFilteredOptions(options);
    else {
      const filtered = options.filter((o) =>
        (o.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o.mobileNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        if (!multiple && !selectedOption) setSearchTerm('');
        else if (!multiple && selectedOption) setSearchTerm(`${selectedOption.fullName} (${selectedOption.mobileNumber || ''})`);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption, multiple]);

  const loadPartners = async () => {
    if (!companyId || !branchId) return;
    try {
      setLoading(true);
      const response = await PartnerService.getPartnerDropdown(companyId, branchId);
      if (response.success) setOptions(response.data || []);
      else setOptions([]);
    } catch (err) {
      console.error('Partner dropdown load error', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true);
    if (!term && !multiple) {
      setSelectedOption(null);
      if (onChange) onChange({ target: { name, value: null, partnerData: null } });
    }
  };

  const handleOptionSelect = (option) => {
    if (multiple) {
      const exists = selectedOptions.some((s) => s.id === option.id);
      let newSel = exists ? selectedOptions.filter((s) => s.id !== option.id) : [...selectedOptions, option];
      setSelectedOptions(newSel);
      const vals = newSel.map((s) => s.id);
      if (onChange) onChange({ target: { name, value: vals } });
      setSearchTerm('');
    } else {
      setSelectedOption(option);
      setSearchTerm(`${option.fullName} (${option.mobileNumber || ''})`);
      setIsOpen(false);
      if (onChange) onChange({ target: { name, value: option.id, partnerData: option } });
    }
  };

  const handleClear = () => {
    if (multiple) {
      setSelectedOptions([]);
      if (onChange) onChange({ target: { name, value: [] } });
    } else {
      setSelectedOption(null);
      setSearchTerm('');
      if (onChange) onChange({ target: { name, value: null, partnerData: null } });
    }
    setIsOpen(false);
  };

  return (
    <div className={`partner-by-branch-search-select ${className}`} ref={dropdownRef} style={{ position: 'relative' }}>
      {showLabel && (
        <Form.Label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}

      <div className="position-relative">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => { if (companyId && branchId) setIsOpen(true); }}
          disabled={disabled || !companyId || !branchId}
        />

        {allowClear && (selectedOption || (multiple && selectedOptions.length > 0)) && (
          <button type="button" className="btn btn-sm btn-link position-absolute" style={{ right: 8, top: 6 }} onClick={handleClear}>Clear</button>
        )}

        {isOpen && filteredOptions.length > 0 && (
          <div className="dropdown-menu show w-100" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {filteredOptions.map((opt) => (
              <button key={opt.id} type="button" className="dropdown-item" onClick={() => handleOptionSelect(opt)}>
                {opt.fullName} {opt.mobileNumber ? `(${opt.mobileNumber})` : ''}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerByBranchSearchSelect;
